import { useMemo } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { toast } from "react-toastify";
import { FiMessageSquare, FiSend } from "react-icons/fi";
import {
  useGetSmsConfigQuery,
  useSendSmsMutation,
} from "../../features/sms/smsApi";
import {
  formatE164Display,
  formatUsPhoneTyping,
  phoneDigits,
} from "../../utils/phoneHelpers";
import GridLoader from "react-spinners/GridLoader";

const labelClass = "text-sm font-semibold text-gray-800 mb-2 block";
const inputClass =
  "w-full py-2.5 px-4 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#dbb685] focus:border-[#dbb685] transition-all shadow-sm hover:border-gray-300";
const errorClass = "text-rose-500 text-xs mt-1.5";
const SMS_MAX_LENGTH = 1600;

const mobileSchema = yup
  .string()
  .required("Recipient phone number is required")
  .test(
    "mobile-digits",
    "Enter a valid 10-digit US mobile number",
    (v) => phoneDigits(v ?? "").length === 10
  );

const smsSchema = yup.object({
  mobile: mobileSchema,
  body: yup
    .string()
    .required("Message is required")
    .trim()
    .max(SMS_MAX_LENGTH, `Message must be at most ${SMS_MAX_LENGTH} characters`),
});

type SmsFormData = yup.InferType<typeof smsSchema>;

const defaultValues: SmsFormData = {
  mobile: "",
  body: "",
};

const SmsSendPage = () => {
  const { data: configResponse, isLoading: configLoading } =
    useGetSmsConfigQuery();
  const [sendSms, { isLoading: isSending }] = useSendSmsMutation();

  const fromDisplay = useMemo(
    () => formatE164Display(configResponse?.data?.from ?? null),
    [configResponse?.data?.from]
  );

  const methods = useForm<SmsFormData>({
    resolver: yupResolver(smsSchema),
    defaultValues,
    mode: "onBlur",
  });

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors },
  } = methods;

  const bodyValue = watch("body") ?? "";
  const charCount = bodyValue.length;
  const segmentHint =
    charCount === 0
      ? ""
      : charCount <= 160
        ? "1 segment (GSM)"
        : `${Math.ceil(charCount / 153)} segments (approx.)`;

  const onSubmit = async (form: SmsFormData) => {
    try {
      const result = await sendSms({
        mobile_number: phoneDigits(form.mobile),
        body: form.body.trim(),
      }).unwrap();

      toast.success(
        result?.message || "SMS sent successfully"
      );
      reset(defaultValues);
    } catch (err: unknown) {
      const apiErr = err as { data?: { message?: string } };
      const message =
        apiErr?.data?.message ||
        (typeof apiErr === "object" && apiErr !== null && "message" in apiErr
          ? String((apiErr as { message?: string }).message)
          : "Failed to send SMS");
      toast.error(message);
    }
  };

  if (configLoading) {
    return (
      <div className="flex items-center justify-center min-h-[320px]">
        <GridLoader color="#dbb685" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-3xl mx-auto">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#dbb685]/15 text-[#b8956a]">
            <FiMessageSquare size={22} />
          </span>
          <h1 className="text-2xl font-bold text-gray-900">Send SMS</h1>
        </div>
        <p className="text-sm text-gray-600 ml-[52px]">
          Send a one-off text message to a US mobile number. Messages are sent
          from your configured Twilio number.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} noValidate className="p-6 md:p-8 space-y-6">
            <div>
              <label className={labelClass}>From</label>
              <input
                type="text"
                readOnly
                value={fromDisplay}
                className={`${inputClass} bg-gray-50 text-gray-500 cursor-not-allowed`}
                aria-describedby="from-hint"
              />
              <p id="from-hint" className="text-xs text-gray-500 mt-1.5">
                USA-only outbound service (Twilio)
              </p>
            </div>

            <div>
              <label htmlFor="mobile" className={labelClass}>
                To <span className="text-rose-500">*</span>
              </label>
              <div className="flex rounded-lg border border-gray-200 overflow-hidden shadow-sm focus-within:ring-2 focus-within:ring-[#dbb685] focus-within:border-[#dbb685]">
                <span className="inline-flex items-center px-4 bg-gray-50 border-r border-gray-200 text-sm font-medium text-gray-600">
                  +1
                </span>
                <Controller
                  name="mobile"
                  control={control}
                  render={({ field }) => (
                    <input
                      id="mobile"
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel-national"
                      placeholder="(555) 555-5555"
                      className="flex-1 py-2.5 px-4 text-sm text-gray-700 border-0 focus:outline-none focus:ring-0"
                      value={field.value}
                      onChange={(e) =>
                        field.onChange(formatUsPhoneTyping(e.target.value))
                      }
                      onBlur={field.onBlur}
                    />
                  )}
                />
              </div>
              {errors.mobile?.message && (
                <p className={errorClass}>{errors.mobile.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="body" className={labelClass}>
                Message <span className="text-rose-500">*</span>
              </label>
              <textarea
                id="body"
                rows={6}
                placeholder="Enter the text to send"
                className={`${inputClass} resize-y min-h-[140px] ${
                  errors.body ? "border-rose-300 focus:ring-rose-200 focus:border-rose-400" : ""
                }`}
                maxLength={SMS_MAX_LENGTH}
                {...register("body")}
              />
              <div className="flex justify-between items-start mt-1.5 gap-4">
                <div>
                  {errors.body?.message && (
                    <p className={errorClass}>{errors.body.message}</p>
                  )}
                </div>
                <p
                  className={`text-xs shrink-0 ${
                    charCount > SMS_MAX_LENGTH ? "text-rose-500" : "text-gray-500"
                  }`}
                >
                  {charCount}/{SMS_MAX_LENGTH}
                  {segmentHint ? ` · ${segmentHint}` : ""}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 pt-2 border-t border-gray-100">
              <button
                type="submit"
                disabled={isSending}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white bg-[#dbb685] hover:bg-[#c9a574] disabled:opacity-60 disabled:cursor-not-allowed transition-colors shadow-sm"
              >
                <FiSend size={16} />
                {isSending ? "Sending…" : "Send SMS"}
              </button>
              <button
                type="button"
                disabled={isSending}
                onClick={() => reset(defaultValues)}
                className="px-5 py-2.5 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-60 transition-colors"
              >
                Clear
              </button>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export default SmsSendPage;
